/**
 * Confirmação de envio ao prontuário — bottom sheet mobile.
 * Conteúdo no topo; botões ancorados próximo ao bottom da tela (acima da nav bar).
 */

import { Close, Send } from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Stack,
  SwipeableDrawer,
  Typography,
  useTheme,
} from "@mui/material";
import type { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { usePrivacyContext } from "@/contexts/PrivacyContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { PostWearableObservationResource } from "@/services/fhir/observation/PostWearableObservation";
import {
  WEARABLE_LOINC_LIST,
  buildWearableObservations,
  hasWearableValueForKey,
  type WearableShareKey,
} from "@/services/fhir/observation/wearableObservationBuilder";
import type { IGoogleHealthFitnessBundle } from "@/services/google/health";
import { formatarDadosGoogleHealth } from "@/utils/wearableFitFormat";
import {
  getMobileSheetSlideProps,
  getMobileSheetTransitionDuration,
} from "@/components/common/mobileSheetMotion";

interface FitnessDataShareConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  fitnessBundle: IGoogleHealthFitnessBundle | null;
  selectedDate: Dayjs;
  selectedKeys: Record<string, boolean>;
  compact?: boolean;
  onSuccess?: () => void;
}

export function FitnessDataShareConfirmDialog({
  open,
  onClose,
  fitnessBundle,
  selectedDate,
  selectedKeys,
  compact = false,
  onSuccess,
}: FitnessDataShareConfirmDialogProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useSessionContext();
  const { isSharing } = usePrivacyContext();
  const [sending, setSending] = useState(false);

  /** Reserva espaço para a MobileBarNavigation flutuante no rodapé branco. */
  const mobileNavClearance =
    "calc(96px + env(safe-area-inset-bottom, 0px))";

  const fit = useMemo(
    () => formatarDadosGoogleHealth(fitnessBundle, selectedDate),
    [fitnessBundle, selectedDate]
  );

  const selectedItems = useMemo(
    () =>
      WEARABLE_LOINC_LIST.filter(
        (def) => hasWearableValueForKey(fit, def.key) && selectedKeys[def.key]
      ),
    [fit, selectedKeys]
  );

  const handleConfirm = async () => {
    const keys = selectedItems.map((d) => d.key);
    if (!keys.length) {
      toast.error("Selecione ao menos um dado com valor para enviar.");
      return;
    }

    const observations = buildWearableObservations({
      fit,
      selectedDate,
      patientId: user.username,
      patientDisplay: user.name || user.username,
      keys: keys as WearableShareKey[],
    });

    if (!observations.length) {
      toast.error("Nenhuma observação válida para enviar.");
      return;
    }

    setSending(true);
    try {
      for (const obs of observations) {
        await PostWearableObservationResource(obs);
      }
      toast.success(
        `${observations.length} observação(ões) enviada(s) com sucesso.`
      );
      onClose();
      onSuccess?.();
    } catch {
      /* toast já em PostWearableObservation */
    } finally {
      setSending(false);
    }
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={sending ? () => undefined : onClose}
      onOpen={() => undefined}
      disableSwipeToOpen
      swipeAreaWidth={0}
      transitionDuration={getMobileSheetTransitionDuration()}
      SlideProps={getMobileSheetSlideProps(theme)}
      ModalProps={{
        keepMounted: false,
        sx: { zIndex: theme.zIndex.modal + 2 },
        BackdropProps: {
          sx: { bgcolor: alpha(theme.palette.common.black, 0.45) },
          transitionDuration: getMobileSheetTransitionDuration(),
        },
      }}
      PaperProps={{
        sx: {
          left: 0,
          right: 0,
          bottom: 0,
          height: compact ? "58dvh" : "50vh",
          minHeight: compact ? 380 : 320,
          maxHeight: compact ? "72dvh" : "60vh",
          borderRadius: "20px 20px 0 0",
          bgcolor: "background.paper",
          boxShadow: "0 -10px 40px rgba(0, 0, 0, 0.14)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          height: "100%",
          bgcolor: "background.paper",
        }}
      >
        {/* Alça */}
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 99,
              bgcolor: "divider",
            }}
          />
        </Box>

        {/* Cabeçalho */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 2,
            pb: 1.25,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Send sx={{ fontSize: 18, color: "primary.main" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.25}>
              Enviar ao prontuário
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedDate.format("DD/MM/YYYY")}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={sending}
            aria-label="Fechar"
            sx={{ color: "text.secondary" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        {/* Conteúdo no topo — sem scroll */}
        <Box sx={{ flexShrink: 0, px: 2, py: 1.5 }}>
          {!isSharing ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: compact ? "0.8rem" : undefined, lineHeight: 1.5 }}
            >
              Para registrar observações no prontuário, é necessário permitir
              compartilhamento nas configurações de privacidade.{" "}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  onClose();
                  navigate("/privacy");
                }}
              >
                Abrir privacidade
              </Link>
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: compact ? "0.8rem" : undefined, lineHeight: 1.45 }}
              >
                Confirmar envio de{" "}
                <strong>{selectedItems.length}</strong>{" "}
                {selectedItems.length === 1 ? "medição" : "medições"} para o seu
                prontuário?
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.4, display: "block" }}
              >
                Profissionais e unidades com compartilhamento concedido poderão
                visualizar estas informações.
              </Typography>

              {selectedItems.length > 0 && (
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
                  {selectedItems.map((item) => (
                    <Chip
                      key={item.key}
                      label={item.text}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        fontSize: compact ? "0.68rem" : "0.72rem",
                        height: 26,
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </Box>

        {/* Empurra os botões para o bottom do sheet */}
        <Box sx={{ flex: 1, minHeight: 12 }} />

        {/* Botões ancorados no bottom da tela (padding acima da nav bar) */}
        <Stack
          spacing={1}
          sx={{
            flexShrink: 0,
            px: 2,
            pt: 1.25,
            pb: compact ? mobileNavClearance : "max(20px, env(safe-area-inset-bottom))",
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          {isSharing && (
            <Button
              fullWidth
              variant="contained"
              size="large"
              disableElevation
              onClick={handleConfirm}
              disabled={sending || selectedItems.length === 0}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                py: 1.1,
                fontSize: compact ? "0.875rem" : "0.9375rem",
              }}
            >
              {sending ? "Enviando..." : "Confirmar envio"}
            </Button>
          )}
          <Button
            fullWidth
            variant="text"
            onClick={onClose}
            disabled={sending}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "text.secondary",
              py: 0.75,
            }}
          >
            Cancelar
          </Button>
        </Stack>
      </Stack>
    </SwipeableDrawer>
  );
}
